import { useCallback, useEffect, useRef, useState } from 'react';
import { CURRENT_PORTAL_VERSION } from '../../constants/portalVersion.js';
import './walkthrough.css';

/**
 * WalkthroughModal — slideshow shown on first login after a portal version bump.
 *
 * Pure presentation: the parent (WalkthroughGate) owns the slides array and
 * the commit-to-backend flow. This component just walks through the deck and
 * calls onComplete when the user finishes (or skips).
 *
 * @param {{
 *   slides: Array<{ image: string, title: string, body: string }>,
 *   onComplete: () => Promise<void> | void,
 *   onSkip:     () => Promise<void> | void,
 * }} props
 */
const WalkthroughModal = ({ slides, onComplete, onSkip }) => {
  const [index, setIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const nextBtnRef = useRef(null);
  const cardRef = useRef(null);

  const total = slides.length;
  const isLast = index === total - 1;
  const slide = slides[index];

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(total - 1, i + 1));
  }, [total]);

  const finish = useCallback(async (handler) => {
    if (saving) return;
    setError(null);
    setSaving(true);
    try {
      const result = await handler();
      if (result && result.error) {
        setError(result.error);
        setSaving(false);
      }
    } catch (err) {
      console.error('[Walkthrough] commit failed:', err);
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  }, [saving]);

  const handleDone = useCallback(() => finish(onComplete), [finish, onComplete]);
  const handleSkip = useCallback(() => finish(onSkip), [finish, onSkip]);

  useEffect(() => {
    const onKey = (e) => {
      if (saving) return;
      if (e.key === 'ArrowRight' && !isLast) goNext();
      else if (e.key === 'ArrowLeft' && index > 0) goPrev();
      else if (e.key === 'Enter' && isLast) handleDone();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, isLast, index, goNext, goPrev, handleDone]);

  useEffect(() => {
    const t = setTimeout(() => nextBtnRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [index]);

  if (!slide) return null;

  return (
    <div className="wt-backdrop" role="presentation">
      <div
        ref={cardRef}
        className="wt-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wt-title"
      >
        <div className="wt-header">
          <div className="wt-header__text">
            <p className="wt-header__eyebrow">Portal updated</p>
            <h3 className="wt-header__title">What&rsquo;s new in PalCare Portal {CURRENT_PORTAL_VERSION}</h3>
            <p className="wt-header__subtitle">
              A quick look at what&rsquo;s changed since your last visit
              {total > 1 ? ` · ${index + 1} of ${total}` : null}
            </p>
            <p className={"wt-header__subtitle"} style={{fontSize: "12px", display: "inline-block"}}>Viewed by: {slide.minRoleWeight > 0 ? slide.minRoleName : "Everyone"}</p>
          </div>
          {!isLast ? (
            <button
              type="button"
              className="wt-header__skip"
              onClick={handleSkip}
              disabled={saving}
            >
              Skip tour
            </button>
          ) : null}
        </div>

        <div className="wt-body">
          <div className="wt-slide" key={index}>
            {slide.image ? (
              <div className="wt-image-wrap">
                <img className="wt-image" src={slide.image} alt="" />
              </div>
            ) : null}
            <div className="wt-content">
              <h2 id="wt-title" className="wt-title">{slide.title}</h2>
              <p className="wt-text">{slide.body}</p>
            </div>
          </div>
        </div>

        {error ? (
          <div className="wt-error" role="alert">{error}</div>
        ) : null}

        <div className="wt-footer">
          {total > 1 ? (
            <div className="wt-dots" aria-hidden="true">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`wt-dot ${i === index ? 'is-active' : ''}`}
                  onClick={() => !saving && setIndex(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          ) : <span />}

          <div className="wt-actions">
            {index > 0 ? (
              <button
                type="button"
                className="wt-btn wt-btn--secondary"
                onClick={goPrev}
                disabled={saving}
              >
                Back
              </button>
            ) : null}
            {isLast ? (
              <button
                ref={nextBtnRef}
                type="button"
                className="wt-btn wt-btn--primary"
                onClick={handleDone}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Got it'}
              </button>
            ) : (
              <button
                ref={nextBtnRef}
                type="button"
                className="wt-btn wt-btn--primary"
                onClick={goNext}
                disabled={saving}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalkthroughModal;
